import React, { useState } from 'react';
import backgroundImage from 'assets/images/service-reminders-mobile-preview.png';
import { Card, IconText } from 'shared/components';
import {
  Box,
  Button,
  Flex,
  Heading,
  Icon,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooterButtons,
  ModalCloseButton,
  Text,
  TextInput,
  useDisclosure,
  Collapse,
  OrderedList,
  ListItem,
} from '@televet/kibble-ui';
import useClinicUser from 'shared/hooks/useClinicUser';
import { useValidation } from 'shared/hooks';
import { useBitwerxData } from 'shared/providers/IntegrationsProvider';

export const ReminderOnboardingBanner = (): JSX.Element => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { clinicUser, hasAdminRoleAtCurrentClinic } = useClinicUser();
  const { validateEmail } = useValidation();

  const { bitwerxPending, requestBitwerx, bitwerxLoading } = useBitwerxData();

  const [hasInitiatedIntegration, setHasInitiatedIntegration] = useState(bitwerxPending);
  const [invalidEmail, setInvalidEmail] = useState(false);

  const [warnAdminOnly, setWarnAdminOnly] = useState(false);

  const [email, setEmail] = useState<string>(clinicUser?.email || '');

  const handleEmailChange = (e: { target: { value: React.SetStateAction<string> } }): void => {
    setInvalidEmail(false);
    setEmail(e.target.value);
  };

  const handleSubmit = (): void => {
    if (validateEmail(email).isValid) {
      requestBitwerx({
        clinicContactEmail: email,
        forPayments: false,
        callback: () => {
          onClose();
          setHasInitiatedIntegration(true);
        },
      });
    } else {
      setInvalidEmail(true);
    }
  };

  const handleOpen = (): void => {
    if (hasAdminRoleAtCurrentClinic) {
      onOpen();
    } else {
      setWarnAdminOnly(true);
    }
  };

  return (
    <Card backgroundImage={backgroundImage} backgroundPosition="top right" w={552}>
      <Heading size="md" w="320px">
        Simplify your workflow by sending service reminders with Flow
      </Heading>
      <Box my={5} w="320px">
        <IconText name="clock">Setting up reminders is fast and easy</IconText>
        <IconText name="send" align="top">
          Customize timing and method of delivery for each service
        </IconText>
        <IconText name="message" align="top">
          Quickly see and respond to appointment requests in conversations
        </IconText>
      </Box>

      {hasInitiatedIntegration ? (
        <Box w="320px" p={3} pt={2} backgroundColor="background.info" borderRadius="md">
          <Text size="sm">
            We’ve notified Bitwerx of your request and they will be reaching out to continue the integration process. If
            you have questions, please reach out to support@otto.vet.
          </Text>
        </Box>
      ) : (
        <>
          <Button onClick={handleOpen}>Get Started</Button>
          <Collapse in={warnAdminOnly} animateOpacity>
            <Text size="sm" as="div" mt={2} variant="danger">
              You must be an admin to enable service reminders
            </Text>
          </Collapse>
        </>
      )}

      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalHeader>Sync Service Reminders</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text as="p" mb={3}>
            In order to enable service reminders, we need to set up additional integrations with your PIMS.
          </Text>
          <OrderedList mb={6}>
            <ListItem>Submit an email below</ListItem>
            <ListItem>
              Bitwerx, our integration partners, will reach out to the provided email and walk you through the
              integration process
            </ListItem>
            <ListItem>Otto will begin syncing your reminder data and notify you when syncing is complete</ListItem>
          </OrderedList>

          <Box mb={6}>
            <Text fontWeight="bold" pb={2} as="div">
              Preferred Email
            </Text>
            <TextInput
              type="email"
              value={email}
              autoFocus={true}
              isRequired={true}
              onChange={handleEmailChange}
              isInvalid={invalidEmail}
              errorText="Invalid Email"
            />
          </Box>

          <Box backgroundColor="background.info" borderRadius="md" py={2} px={3}>
            <Flex>
              <Box flexShrink={0} mr={3}>
                <Icon name="infoCircle" variant="info" />
              </Box>
              <Box>
                <Text fontWeight="bold" as="p">
                  Syncing does NOT enable reminders
                </Text>
                <Text as="p" maxWidth="340px">
                  Once syncing is complete, you will be able to configure your service reminders. Flow will only start
                  sending service reminders once you choose to enable them.
                </Text>
              </Box>
            </Flex>
          </Box>
        </ModalBody>
        <ModalFooterButtons onSubmit={handleSubmit} isLoading={bitwerxLoading} />
      </Modal>
    </Card>
  );
};
